// Pure CSV / XLSX serializer for the bookings export feature.
// No React, no Supabase imports: easy to unit-test, easy to lazy-load.

export type ExportFormat = "csv" | "xlsx";
export type ExportPreset = "full" | "reimportable";

export interface ExportBooking {
  booking_id: string;
  short_id: string | null;
  booking_date: string; // YYYY-MM-DD
  booking_time: string; // HH:MM
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  listing_id: string | null;
  listings?: { name: string | null; price?: number | null } | null;
  number_of_pax: number | null;
  total_paid: number | null;
  net_amount?: number | null;
  status: string;
  payment_method: string | null;
  is_imported?: boolean | null;
  notes: string | null;
  created_at?: string | null;
}

// Sanitise a single cell against CSV-injection.
// Excel / Sheets treat a cell starting with =, +, -, @, \t, \r as a formula.
// We defang by prefixing with a single quote; the quote is invisible in the
// rendered cell but neutralises the formula.
export function escapeCsvCell(value: unknown): string {
  if (value == null) return "";
  const s = String(value);
  if (s.length === 0) return "";
  const first = s.charAt(0);
  if (first === "=" || first === "+" || first === "-" || first === "@" || first === "\t" || first === "\r") {
    return "'" + s;
  }
  return s;
}

// Full export = every column we have. Useful for backup, accounting, debugging.
// Re-importable = the headers + formats our importer accepts, so the file
// round-trips back into Dibs without edits.
type Row = Record<string, string | number | null>;

function fullRow(b: ExportBooking): Row {
  return {
    "Booking ID": b.booking_id,
    "Order ID": b.short_id ?? "",
    "Date": b.booking_date,
    "Time": b.booking_time,
    "Customer Name": b.customer_name,
    "Email": b.customer_email,
    "Phone": b.customer_phone ?? "",
    "Listing ID": b.listing_id ?? "",
    "Service": b.listings?.name ?? "",
    "Pax": b.number_of_pax ?? "",
    "Paid": b.total_paid ?? "",
    "Net amount": b.net_amount ?? "",
    "Status": b.status,
    "Payment method": b.payment_method ?? "",
    "Is imported": b.is_imported ? "true" : "false",
    "Notes": b.notes ?? "",
    "Created at": b.created_at ?? "",
  };
}

function reimportableRow(b: ExportBooking): Row {
  // Headers and formats match the import template exactly so the user can
  // edit and re-import this CSV without any further mapping.
  return {
    "Date": b.booking_date,
    "Time": b.booking_time,
    "Customer Name": b.customer_name,
    "Email": b.customer_email,
    "Phone": b.customer_phone ?? "",
    "Service": b.listings?.name ?? "",
    "Pax": b.number_of_pax ?? "",
    "Paid": b.total_paid ?? "",
    "Status": b.status,
    "Notes": b.notes ?? "",
  };
}

export function buildExportRows(
  bookings: ExportBooking[],
  preset: ExportPreset,
): { headers: string[]; rows: Row[] } {
  const mapper = preset === "full" ? fullRow : reimportableRow;
  const rows = bookings.map(mapper);
  const headers = rows.length > 0 ? Object.keys(rows[0]) : Object.keys(mapper({
    booking_id: "", short_id: null, booking_date: "", booking_time: "",
    customer_name: "", customer_email: "", customer_phone: null,
    listing_id: null, listings: null, number_of_pax: null, total_paid: null,
    status: "", payment_method: null, is_imported: false, notes: null,
  }));
  return { headers, rows };
}

// RFC 4180 + UTF-8 BOM so Excel opens it cleanly with non-ASCII chars.
// CRLF line endings for max compatibility.
export function toCsv(headers: string[], rows: Row[]): string {
  const escape = (raw: unknown): string => {
    const s = escapeCsvCell(raw);
    // RFC 4180: any field containing CR, LF, double quote, or comma must be
    // wrapped in double quotes; embedded quotes are doubled.
    if (/[",\r\n]/.test(s)) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines: string[] = [];
  lines.push(headers.map(escape).join(","));
  for (const r of rows) {
    lines.push(headers.map((h) => escape(r[h])).join(","));
  }
  return "﻿" + lines.join("\r\n");
}

export function sanitizeFilename(name: string): string {
  return name
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60) || "business";
}

export function buildFilename(
  businessName: string,
  format: ExportFormat,
  date = new Date(),
): string {
  const isoDate = date.toISOString().slice(0, 10);
  return `dibs-bookings-${sanitizeFilename(businessName)}-${isoDate}.${format}`;
}

// Browser-side download helper. Wraps the content in a Blob and clicks an
// invisible anchor. Returns nothing; caller handles success/failure outside.
export function downloadBlob(content: BlobPart, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so Safari finishes the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
